import { defaultPeriodValue } from '@/react/edge/edge-stacks/components/RetryDeployPeriod';
import { getDefaultStaggerConfig } from '@/react/edge/edge-stacks/components/StaggerFieldset.types';
import {
  DeploymentType as EdgeStackDeploymentType,
  EdgeStack,
} from '@/react/edge/edge-stacks/types';
import { getDefaultRelativePathModel } from '@/react/portainer/gitops/RelativePathFieldset/types';

import { Workflow, WorkflowArtifact } from '../types';
import { Artifact, FormValues } from '../wizard/types';
import { DeploymentType } from '../wizard/detectDeploymentType';

export function toEditFormValues(
  workflow: Workflow,
  edgeStacks: Array<EdgeStack>
): FormValues {
  // Edge stacks are handed out in order to the artifacts that have one; a
  // reconciled artifact has none and carries its own configuration instead.
  let nextEdgeStack = 0;
  const artifacts = workflow.artifacts.map((artifact) =>
    artifact.type === 'gitopsDeployment'
      ? toReconciledArtifactValues(artifact)
      : toArtifactValues(artifact, edgeStacks[nextEdgeStack++])
  );
  const sourceIds = [
    ...new Set(
      artifacts.flatMap((artifact) =>
        artifact.files.map((file) => file.sourceId)
      )
    ),
  ];

  // What the workflow runs on now. Switching it to the reconciliation engine
  // migrates the workflow; the reverse is not offered, because the engine
  // adopts what the Edge stacks deployed.
  const hasGitopsDeployment = workflow.artifacts.some(
    (artifact) => artifact.type === 'gitopsDeployment'
  );

  // Recorded on each deployment rather than the workflow, so the first one
  // that has it speaks for all of them.
  const reconcileInterval =
    workflow.artifacts.find((artifact) => artifact.reconcile?.interval)
      ?.reconcile?.interval ?? '5m';

  return {
    name: workflow.name,
    engine: hasGitopsDeployment ? 'reconcile' : 'edgeStack',
    reconcile: { interval: reconcileInterval },
    sources: sourceIds,
    artifacts,
  };
}

/**
 * A reconciled artifact has no Edge stack: what it was created with lives on the
 * deployment, and the fields that belong to the other engine take their defaults
 * so the form has something valid to hold.
 */
function toReconciledArtifactValues(artifact: WorkflowArtifact): Artifact {
  return {
    name: artifact.name,
    dependencies: artifact.dependencies ?? [],
    deploymentType: DeploymentType.Kubernetes,
    files: artifact.files.flatMap((file) =>
      file.sourceId
        ? [
            {
              sourceId: file.sourceId,
              path: file.path ?? '',
              ref: file.ref ?? '',
            },
          ]
        : []
    ),
    targets: artifact.target?.edgeGroupIds ?? [],
    environmentGroups: artifact.target?.environmentGroupIds ?? [],
    environments: artifact.target?.environmentIds ?? [],
    // Read back rather than defaulted: an edit that changes the artifact's
    // paths sends the chart configuration with them, so a form that started
    // blank would save the chart's namespace away.
    //
    // The values files are not restored because the artifact does not carry
    // them separately - they were appended to its file list when it was
    // created, and come back as ordinary paths.
    helm: {
      enabled: !!artifact.helm,
      valuesFiles: [],
      namespace: artifact.helm?.namespace ?? '',
    },
    config: {
      envVars: [],
      prePullImage: false,
      retryDeploy: false,
      retryPeriod: defaultPeriodValue,
      parallelConfig: getDefaultStaggerConfig(),
      relativePath: getDefaultRelativePathModel(),
      useManifestNamespaces: artifact.useManifestNamespaces ?? false,
      kubernetesPrune: artifact.reconcile?.prune ?? true,
      kubernetesForceApply: artifact.reconcile?.forceApply ?? false,
      driftMode: artifact.reconcile?.driftMode ?? 'warn',
    },
  };
}

function toArtifactValues(
  artifact: WorkflowArtifact,
  edgeStack: EdgeStack
): Artifact {
  return {
    name: artifact.name,
    dependencies: artifact.dependencies ?? [],
    deploymentType: toWizardDeploymentType(edgeStack.DeploymentType),
    files: artifact.files.flatMap((file) =>
      file.sourceId
        ? [
            {
              sourceId: file.sourceId,
              path: file.path ?? '',
              ref: file.ref ?? '',
            },
          ]
        : []
    ),
    targets: artifact.target?.edgeGroupIds ?? [],
    environmentGroups: artifact.target?.environmentGroupIds ?? [],
    environments: [],
    // An Edge stack has no chart of its own; a chart is a reconciled artifact.
    helm: { enabled: false, valuesFiles: [], namespace: '' },
    config: {
      envVars: edgeStack.EnvVars ?? [],
      prePullImage: edgeStack.PrePullImage,
      retryDeploy: edgeStack.RetryDeploy,
      retryPeriod: edgeStack.RetryDeploy
        ? edgeStack.RetryPeriod
        : defaultPeriodValue,
      parallelConfig: edgeStack.StaggerConfig ?? getDefaultStaggerConfig(),
      registryId: edgeStack.Registries?.[0],
      relativePath: {
        AlwaysCloneGitRepoForRelativePath:
          edgeStack.AlwaysCloneGitRepoForRelativePath,
        FilesystemPath: edgeStack.FilesystemPath,
        PerDeviceConfigsGroupMatchType:
          edgeStack.PerDeviceConfigsGroupMatchType,
        PerDeviceConfigsMatchType: edgeStack.PerDeviceConfigsMatchType,
        PerDeviceConfigsPath: edgeStack.PerDeviceConfigsPath,
        SupportPerDeviceConfigs: edgeStack.SupportPerDeviceConfigs,
        SupportRelativePath: edgeStack.SupportRelativePath,
      },
      useManifestNamespaces: edgeStack.UseManifestNamespaces,
      kubernetesPrune: edgeStack.KubernetesOptions?.Prune ?? true,
      driftMode: 'warn',
      kubernetesForceApply: edgeStack.KubernetesOptions?.ForceApply ?? false,
    },
  };
}

function toWizardDeploymentType(
  deploymentType: EdgeStackDeploymentType
): DeploymentType {
  return deploymentType === EdgeStackDeploymentType.Compose
    ? DeploymentType.Compose
    : DeploymentType.Kubernetes;
}
